import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Button,
    HierarchyList,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader
} from 'common/components';
import { RootState } from 'common/redux/core/root.reducer';

import { getHierarchyCheckboxListColDefs } from 'modules/configuration/config';
import { configurationActions } from 'modules/configuration/redux';
import {
    getHierarchicalDataFn,
    getSelectedIndicatorsForAdd
} from 'modules/configuration/redux/configuration.selector';
import './AddNewIndicator.scss';
import { PagedRequest } from 'common/models/data/pagedRequest.model';
import { defaultPageRequest } from 'modules/configuration/constants/add-new-indicator';
import { ApiStatus, MessageType } from 'common/enums';
import { appStateActions } from 'common/redux/app-state';
import { DEFAULT_ERROR_MESSAGE, FAILURE_HEADER } from 'common/config';
import { EntityVM } from 'common/models/data/entity.model';
import { ConfigurationTypes } from 'modules/configuration/enums/configuration.enum';

type AddNewIndicatorProps = {
    isVisible: boolean;
    closePopupHandler: () => void;
    allowedCountOfIndicators?: number;
    updateSelectedCheckbox: (isSelected: boolean, data: any) => void;
    modalDetails: any;
    onSave: () => void;
    configurationType: ConfigurationTypes;
};

export default function AddNewIndicator({
    isVisible,
    closePopupHandler,
    updateSelectedCheckbox,
    allowedCountOfIndicators,
    modalDetails,
    onSave,
    configurationType
}: AddNewIndicatorProps) {
    const pagedRequest = useRef<PagedRequest>(defaultPageRequest);
    const hierarchicalData = useSelector((state: RootState) => getHierarchicalDataFn(state));
    const selectedIndicators = useSelector((state: RootState) =>
        getSelectedIndicatorsForAdd(state)
    );

    const dispatch = useDispatch();

    useEffect(() => {
        if (isVisible) {
            dispatch(configurationActions.getCategories(pagedRequest.current));
        }
    }, [dispatch, isVisible]);

    /**
     * Following function will handle expantion toggle
     * @param rowData is the row which is toggle expantion
     */
    const handleExpansionToggleCallback = (rowData: EntityVM) => {
        try {
            dispatch(
                configurationActions.toggleHierarchicalDataExpansion({
                    data: rowData,
                    configurationType: configurationType
                })
            );
        } catch (error: unknown) {
            dispatch(
                appStateActions.showMessageModal({
                    isVisible: true,
                    header: FAILURE_HEADER,
                    message: DEFAULT_ERROR_MESSAGE,
                    type: MessageType.Error
                })
            );
        }
    };

    const handleScroll = useCallback(() => {
        if (
            hierarchicalData?.status !== ApiStatus.LOADING &&
            hierarchicalData?.data &&
            pagedRequest.current.pageNumber + 1 <= hierarchicalData?.data?.pagination?.totalPages
        ) {
            pagedRequest.current.pageNumber = pagedRequest.current.pageNumber + 1;
            dispatch(configurationActions.getCategories(pagedRequest.current));
        }
    }, [dispatch, hierarchicalData?.data, hierarchicalData?.status]);

    return (
        <>
            <Modal
                show={isVisible}
                onClose={closePopupHandler}
                style={{ width: '50vw', height: '70vh' }}
                className="add-new-indicator-main-container">
                <ModalHeader>{modalDetails.title}</ModalHeader>
                <ModalBody>
                    <HierarchyList
                        className={'add-new-indicator-list'}
                        isNoDataVisible={false}
                        data={hierarchicalData?.data?.data ?? []}
                        selectorType={'checkbox'}
                        columnDefs={getHierarchyCheckboxListColDefs(
                            true,
                            updateSelectedCheckbox,
                            handleExpansionToggleCallback
                        )}
                        handleScroll={handleScroll}
                    />
                </ModalBody>
                <ModalFooter>
                    <Button
                        size="medium"
                        type="button"
                        onClick={onSave}
                        disabled={
                            allowedCountOfIndicators
                                ? selectedIndicators?.length > allowedCountOfIndicators
                                : false
                        }>
                        Save
                    </Button>
                    <Button
                        size="medium"
                        variant="secondary"
                        type="button"
                        onClick={closePopupHandler}>
                        Cancel
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
}
